import { useState, useMemo, useEffect, useRef, ReactElement } from 'react';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useTemplateCategories } from './hooks/use-template-categories';
import { useFilteredTemplates } from './hooks/use-filtered-templates';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCampaignTemplates } from '@/store/slices/campaign-templates/selectors';
import {
  selectCreatingCampaign,
  selectFavoriteTemplates,
  selectRecentTemplates,
} from '@/store/slices/campaigns/selectors';
import LoadingSpinner from '@ui/loading-spinner/loading-spinner';
import CategorySidebar from './components/category-sidebar';
import SuiteCard from './components/suite-card';
import TemplateGrid from './components/template-grid';
import TemplatePreview from './components/template-preview';
import { CampaignTemplate } from '@/connection/campaigns/fetch-campaign-templates';
import classNames from 'classnames';
import classes from './create-campaign-flow.module.css';
import { useNavigate } from 'react-router-dom';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { addFavoriteTemplate, loadRecentTemplates, removeFavoriteTemplate } from '@/store/slices/campaigns';

type ModalStep = 'suites' | 'templates' | 'preview';
type PreviewSize = 'desktop' | 'tablet' | 'mobile';

interface TemplateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateUuid: string, category: string) => void;
  loading: boolean;
}

export default function TemplateSelectionModal({
  isOpen,
  onClose,
  onSelectTemplate,
  loading,
}: TemplateSelectionModalProps) {
  const [step, setStep] = useState<ModalStep>('suites');
  const [sidebarCategory, setSidebarCategory] = useState<string>('all');
  const [selectedSuite, setSelectedSuite] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [previewedTemplate, setPreviewedTemplate] = useState<CampaignTemplate | null>(null);
  const [previewSize, setPreviewSize] = useState<PreviewSize>('desktop');
  const favoriteTemplates = useAppSelector(selectFavoriteTemplates);
  const recentTemplates = useAppSelector(selectRecentTemplates);
  const { categories } = useTemplateCategories();
  const filteredTemplates = useFilteredTemplates(selectedSuite);
  const allTemplates = useAppSelector(selectCampaignTemplates);
  const isCreating = useAppSelector(selectCreatingCampaign);
  const dispatch = useAppDispatch();
  const templateId = previewedTemplate?.uuid;
  const isFavorited = useMemo(
    () => templateId && favoriteTemplates.includes(templateId as string),
    [templateId, favoriteTemplates]
  );
  const navigate = useNavigate();

  // Filter templates by sidebar category selection and search term for the suites grid
  const filteredSuiteTemplates = useMemo(() => {
    let result = allTemplates;
    switch (sidebarCategory) {
      case 'favorites': {
        result = result.filter((t) => favoriteTemplates.includes(t.uuid));
        break;
      }
      case 'recent': {
        result = result.filter((t) => recentTemplates.includes(t.uuid));
        break;
      }
      case 'all': {
        break;
      }
      default: {
        result = result.filter((t) => t.category === sidebarCategory);
      }
    }

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter((t) => t.title?.toLowerCase().includes(lowerSearchTerm));
    }
    return result;
  }, [allTemplates, sidebarCategory, searchTerm, favoriteTemplates, recentTemplates]);

  // Filter templates by search term (only on templates step)
  const searchFilteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) {
      return filteredTemplates;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return filteredTemplates.filter((template) => template.title?.toLowerCase().includes(lowerSearchTerm));
  }, [filteredTemplates, searchTerm]);

  const handleSidebarCategorySelect = (category: string) => {
    setSidebarCategory(category);
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(loadRecentTemplates());
    }
  }, [dispatch, isOpen]);

  const handleTemplateClick = (template: CampaignTemplate) => {
    setSelectedSuite(template.category || '');
    setPreviewedTemplate(template);
    setStep('preview');
    setSearchTerm('');
    setShowSearch(false);
    navigate('.', { replace: false, relative: 'path', state: { template } });
  };

  const handleBack = () => {
    setStep('suites');
    setSelectedSuite('');
    setSearchTerm('');
    setShowSearch(false);
    history.back();
  };

  const handleTemplateEdit = (templateUuid: string) => {
    onSelectTemplate(templateUuid, selectedSuite);
  };

  const handleTemplatePreview = (templateUuid: string) => {
    const template = allTemplates.find((t) => t.uuid === templateUuid);
    if (template) {
      setPreviewedTemplate(template);
      setStep('preview');
      setSearchTerm('');
      setShowSearch(false);
    }
  };

  const handleContinue = () => {
    if (previewedTemplate) {
      onSelectTemplate(previewedTemplate.uuid, selectedSuite);
    }
  };

  const handleSearchToggle = () => {
    if (showSearch) {
      setSearchTerm('');
    }
    setShowSearch(!showSearch);
  };

  const renderHeaderRef = useRef<() => ReactElement>(() => <></>);
  const StableHeader = useMemo(
    () =>
      function StableHeaderComponent() {
        return renderHeaderRef.current();
      },
    []
  );

  const handleClose = () => {
    setStep('suites');
    setSidebarCategory('all');
    setSelectedSuite('');
    setSearchTerm('');
    setShowSearch(false);
    setPreviewedTemplate(null);
    setPreviewSize('desktop');
    onClose();
  };

  const getHeaderTitle = () => {
    if (step === 'suites') {
      return 'Select suite';
    }
    if (step === 'preview' && previewedTemplate) {
      return previewedTemplate.title;
    }
    return `${selectedSuite} Suite`;
  };

  const renderPreviewHeader = () => (
    <Container padding="24px 32px" style={{ borderBottom: '1px solid var(--gray-800)' }}>
      <div className="flex ai-center jc-space-between">
        <div className="flex ai-center gap-12">
          <Button clear onClick={handleBack} className="square-40 flex ai-center" title="Back to templates">
            <Icon name={ICONS.ARROW_LEFT} size={20} color="var(--global-ico-text-2)" />
          </Button>
          <Text tag="h2" className="H3" design="GlobalIcoText1">
            {getHeaderTitle()}
          </Text>
        </div>

        <div className="flex ai-center gap-12">
          <div className={classes.deviceToggleGroup}>
            <Button
              clear
              className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'desktop' })}
              onClick={() => setPreviewSize('desktop')}
              title="Desktop"
            >
              <Icon name={ICONS.DESKTOP} size={16} color="var(--global-ico-text-2)" />
            </Button>
            <Button
              clear
              className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'tablet' })}
              onClick={() => setPreviewSize('tablet')}
              title="Tablet"
            >
              <Icon name={ICONS.TABLET} size={15} color="var(--global-ico-text-2)" />
            </Button>
            <Button
              clear
              className={classNames(classes.deviceToggleButton, { [classes.active]: previewSize === 'mobile' })}
              onClick={() => setPreviewSize('mobile')}
              title="Mobile"
            >
              <Icon name={ICONS.MOBILE} size={13} color="var(--global-ico-text-2)" />
            </Button>
          </div>

          <Button
            clear
            onClick={() => {
              if (!templateId) return;

              if (isFavorited) {
                dispatch(removeFavoriteTemplate(templateId));
              } else {
                dispatch(addFavoriteTemplate(templateId));
              }
            }}
            className="square-40 flex ai-center"
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Icon
              name={isFavorited ? ICONS.HEART_FILLED : ICONS.HEART_FILLED_OUTLINE}
              size={20}
              color={isFavorited ? 'var(--primitives-mode-1-pink-400)' : 'var(--global-ico-text-2)'}
            />
          </Button>

          <Button
            className="L1"
            backgroundColor={getThemeColorCSSVariable()}
            hoverBackgroundColor={getThemeColorsCssMixVariable()}
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            onClick={handleContinue}
            borderRadius="100px"
            padding="10px 24px"
            disabled={isCreating}
            loading={isCreating}
          >
            <Text
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              className="L1 flex ai-center gap-8"
            >
              Continue
              <Icon name={ICONS.ARROW_RIGHT} size={16} />
            </Text>
          </Button>
        </div>
      </div>
    </Container>
  );

  const renderDefaultHeader = () => (
    <Container padding="24px 32px" style={{ borderBottom: '1px solid var(--gray-800)' }}>
      <div className="flex ai-center jc-space-between">
        <div className="flex ai-center gap-12">
          {step === 'templates' && (
            <Button clear onClick={handleBack} className="square-40 flex ai-center" title="Back to suites">
              <Icon name={ICONS.ARROW_LEFT} size={20} color="var(--global-ico-text-2)" />
            </Button>
          )}
          <Text tag="h2" className="H3" design="GlobalIcoText1">
            {getHeaderTitle()}
          </Text>
        </div>

        <div className="flex ai-center gap-8">
          {showSearch && (
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              autoFocus
              className={classes.searchInput}
            />
          )}
          <Button
            clear
            onClick={handleSearchToggle}
            className="square-40 flex ai-center"
            title={showSearch ? 'Close search' : 'Search templates'}
          >
            <Icon
              name={showSearch ? ICONS.CLOSE : ICONS.SEARCH}
              size={showSearch ? 12 : 20}
              color="var(--global-ico-text-2)"
            />
          </Button>
          <Button clear onClick={handleClose} className="square-40 flex ai-center" title="Close" disabled={isCreating}>
            <Icon name={ICONS.CLOSE} size={20} color="var(--global-ico-text-2)" />
          </Button>
        </div>
      </div>
    </Container>
  );

  renderHeaderRef.current = () => (step === 'preview' ? renderPreviewHeader() : renderDefaultHeader());

  return (
    <Modal open={isOpen} onRequestClose={handleClose} size="full" animation="fade" RenderHeaderContent={StableHeader}>
      {step === 'suites' && (
        <div className={classes.templateLayoutSuite}>
          <CategorySidebar
            categories={categories}
            selectedCategory={sidebarCategory}
            onSelectCategory={handleSidebarCategorySelect}
          />
          {loading && filteredSuiteTemplates.length === 0 ? (
            <Container className="flex-column ai-center jc-center" padding="80px">
              <LoadingSpinner />
            </Container>
          ) : filteredSuiteTemplates.length === 0 ? (
            <Container className="flex-column ai-center" padding="80px">
              <Text className="H5" design="GlobalIcoText3" maxWidth="500px">
                {searchTerm ? `No results for "${searchTerm}"` : 'No templates available'}
              </Text>
            </Container>
          ) : (
            <div className={classes.suiteGridContent}>
              {filteredSuiteTemplates.map((template) => (
                <SuiteCard key={template.uuid} template={template} onPreview={handleTemplateClick} />
              ))}
            </div>
          )}
        </div>
      )}
      {step === 'templates' && (
        <div className={classes.templateLayout}>
          <TemplateGrid
            templates={searchFilteredTemplates}
            onEditTemplate={handleTemplateEdit}
            onPreviewTemplate={handleTemplatePreview}
            isCreating={isCreating}
            loading={loading}
            emptyMessage={searchTerm ? `No results for "${searchTerm}"` : 'No templates available for this category'}
          />
        </div>
      )}
      {step === 'preview' && previewedTemplate && (
        <TemplatePreview template={previewedTemplate} previewSize={previewSize} />
      )}
    </Modal>
  );
}
